import { useState, useEffect, useMemo } from "react";
import { Search } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FixtureCard from "@/components/FixtureCard";
import { getFixtureDates, getFixturesByDate, formatDateShort, getSportCategory, getSportCategories } from "@/lib/fixtures";
import { apiGetPredictions, apiGetResults, apiGetFixtures } from "@/lib/storage";

export default function Fixtures() {
  const [activeDate, setActiveDate] = useState("all");
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("all");
  const [view, setView] = useState("compact");
  const [predictions, setPredictions] = useState([]);
  const [results, setResults] = useState([]);
  const [allFixtures, setAllFixtures] = useState([]);
  const dates = getFixtureDates(allFixtures);
  const categories = getSportCategories(allFixtures);

  useEffect(() => {
    apiGetPredictions().then(setPredictions);
    apiGetResults().then(setResults);
    apiGetFixtures().then(setAllFixtures);
    const handler = () => {
      apiGetPredictions().then(setPredictions);
      apiGetResults().then(setResults);
    };
    window.addEventListener("predictions-updated", handler);
    return () => window.removeEventListener("predictions-updated", handler);
  }, []);

  const predMap = {};
  predictions.forEach(p => { predMap[p.fixture_id] = p; });
  const resultMap = {};
  results.forEach(r => { resultMap[r.fixture_id] = r; });

  const filteredFixtures = useMemo(() => {
    let fixtures = activeDate === "all" ? [...allFixtures] : getFixturesByDate(allFixtures, activeDate);
    if (search.trim()) {
      const q = search.toLowerCase();
      fixtures = fixtures.filter(f => f.sport.toLowerCase().includes(q));
    }
    if (sportFilter !== "all") fixtures = fixtures.filter(f => getSportCategory(f.sport) === sportFilter);
    return fixtures;
  }, [activeDate, search, sportFilter, allFixtures]);

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="fixtures-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>ALL FIXTURES</h1>
          <p className="text-xs text-slate-500">{allFixtures.length} matches | +10 points per correct prediction</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView("compact")} className={`px-3 py-1 text-[10px] uppercase tracking-wider rounded-sm ${view === "compact" ? "bg-red-600 text-white" : "bg-white/5 text-slate-400"}`} data-testid="view-compact">List</button>
          <button onClick={() => setView("grid")} className={`px-3 py-1 text-[10px] uppercase tracking-wider rounded-sm ${view === "grid" ? "bg-red-600 text-white" : "bg-white/5 text-slate-400"}`} data-testid="view-grid">Grid</button>
        </div>
      </div>
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="fixture-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sports..." className="pl-9 bg-white/5 border-white/5 h-8 text-xs" />
        </div>
        <Select value={sportFilter} onValueChange={setSportFilter}>
          <SelectTrigger className="w-[180px] bg-white/5 border-white/5 h-8 text-xs" data-testid="sport-filter"><SelectValue placeholder="All Sports" /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-white/10">
            <SelectItem value="all">All Sports</SelectItem>
            {categories.map(cat => <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Tabs value={activeDate} onValueChange={setActiveDate}>
        <div className="overflow-x-auto -mx-2 px-2">
          <TabsList className="bg-white/5 h-auto p-0.5 inline-flex" data-testid="date-tabs">
            <TabsTrigger value="all" className="text-[10px] uppercase tracking-wider px-3 py-1.5 data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm" data-testid="date-tab-all">All</TabsTrigger>
            {dates.map(date => (
              <TabsTrigger key={date} value={date} className="text-[10px] uppercase tracking-wider px-3 py-1.5 data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm" data-testid={`date-tab-${date.replace(/\s/g, "-")}`}>
                {formatDateShort(date)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </Tabs>
      <p className="text-[10px] text-slate-500 uppercase tracking-widest">{filteredFixtures.length} fixture{filteredFixtures.length !== 1 ? "s" : ""}</p>
      {view === "compact" ? (
        <div className="bg-[#111827] border border-white/5 rounded-sm overflow-hidden">
          <div className="red-gradient-line" />
          {filteredFixtures.map(f => <FixtureCard key={f.id} fixture={f} compact prediction={predMap[f.id]} result={resultMap[f.id]} />)}
          {filteredFixtures.length === 0 && <div className="p-8 text-center text-slate-500 text-xs" data-testid="no-fixtures">No fixtures found.</div>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredFixtures.map(f => <FixtureCard key={f.id} fixture={f} prediction={predMap[f.id]} result={resultMap[f.id]} />)}
          {filteredFixtures.length === 0 && <div className="col-span-full p-8 text-center text-slate-500 text-xs" data-testid="no-fixtures">No fixtures found.</div>}
        </div>
      )}
    </div>
  );
}
